import { useCallback, useMemo, useEffect, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { LoadingIndicatorForApp } from 'components';
import {
  ALWAYS_DOWNLOAD_DOCUMENTS,
  DEFAULT_BACK_TO_URL_MOBILEAPP,
} from 'constants';
import {
  BookValuesContext,
  MobileAppValuesContext,
  NetworkStatusValuesContext,
  UserSessionModifiersContext,
  UserSessionValuesContext,
} from 'contexts';
import {
  getIDFromPK,
  getLocalSettingsProxy,
  isUserIdInPercentageThreshold,
  locationLogic,
} from 'utilities';
import { User } from 'types';

export const UserSessionProvider = ({ children }) => {
  const location = useLocation();
  const searchParams = useMemo(
    () =>
      new URLSearchParams(locationLogic.getSearchStringFromLocation(location)),
    [location],
  );

  const { book } = useContext(BookValuesContext);
  const { isOffline } = useContext(NetworkStatusValuesContext);
  const { isMobileApp } = useContext(MobileAppValuesContext);

  const [isSessionExpired, setIsSessionExpired] = useState(false);

  const [bootstrapData, setBootstrapData] = useState();
  const [deviceUUID, setDeviceUUID] = useState();
  const [organization, setOrganization] = useState();
  const [ownedBookPk, setOwnedBookPk] = useState();
  const [backToShelfInfo, setBackToShelfInfo] = useState({});

  const [user, setUser] = useState();
  const [otherUsers, setOtherUsers] = useState();

  const [analyticsMetadata, setAnalyticsMetadata] = useState();

  const [alertMessage, setAlertMessage] = useState();

  const [shouldAlwaysDownloadDocuments, setShouldAlwaysDownloadDocuments] =
    useState();

  const [useElasticsearchInsteadOfLunr, setUseElasticsearchInsteadOfLunr] =
    useState();

  // Helper function to save bootstrap data
  const saveBootstrapData = useCallback(
    ({ data: bootstrapData }) => {
      const localSettings = getLocalSettingsProxy(book.pk);

      localSettings.bootstrap = JSON.stringify(bootstrapData);
      setBootstrapData(bootstrapData);
    },
    [book.pk],
  );

  const getBootstrapData = useCallback(() => {
    fetch(`/api/bootstrap?epub_id=${book.pk}`).then((r) =>
      r.json().then(saveBootstrapData),
    );
  }, [book, saveBootstrapData]);

  useEffect(() => {
    const localSettings = getLocalSettingsProxy(book.pk);

    // Use previous user session information from localstorage, if available, when loading the reader in offline mode.
    if (isOffline) {
      try {
        setBootstrapData(JSON.parse(localSettings.bootstrap));
      } catch (_) {}

      return;
    }

    // When we are online, request latest user session information from the django backend.
    // Update localsettings with the values so that they are available offline.
    getBootstrapData();
  }, [book, isOffline, getBootstrapData]);

  useEffect(() => {
    if (!bootstrapData) {
      return;
    }

    // analytics
    const redShelfData = bootstrapData.rs_data ?? {};
    const metadata = parseAnalyticsMetadata(redShelfData, book);
    setAnalyticsMetadata(metadata);

    // back to shelf
    const backToLabel = bootstrapData.rs_data?.back_to_label;
    const backToUrl = isMobileApp
      ? (bootstrapData.rs_data?.back_to_url ?? DEFAULT_BACK_TO_URL_MOBILEAPP)
      : bootstrapData.rs_data?.back_to_url;
    setBackToShelfInfo({ backToLabel, backToUrl });

    // user
    setUser(
      new User({
        id: getIDFromPK(bootstrapData.user_id, 'PlatformUserNode'),
        profilePk: bootstrapData.user_profile,
        cognitoUUID: bootstrapData.rs_data?.user_cognito_uuid,
        firstName: bootstrapData.user_first_name,
        lastName: bootstrapData.user_last_name,
      }),
    );

    setOrganization(bootstrapData.organization);
    setOwnedBookPk(parseInt(bootstrapData.owned_book_id));
  }, [book, bootstrapData, isMobileApp]);

  /* A group of super-properties that we pass to every event, and associate with
   * a user on a per-session basis.
   * */
  function parseAnalyticsMetadata(data, book) {
    return {
      bookId: book.pk,
      bookIsbn10: data.book_isbn10,
      bookIsbn13: data.book_isbn13,
      bookEisbn10: data.book_eisbn10,
      bookEisbn13: data.book_eisbn13,
      bookTitle: book.title,
      bookPublisherName: data.book_publisher_name,
      courseId: data.course_id,
      courseName: data.course_name,
      departmentId: '',
      departmentName: data.department_name,
      instructorName: data.instructor_name,
      productType: data.product_type,
      sectionId: '',
      rsmSectionId: data.rsm_section_id,
      sectionName: data.section_name,
      termEndDate: data.term_end_date,
      termId: '',
      termName: '',
      termStartDate: data.term_start_date,
      universityId: data.university_id,
      universityName: data.university_name,
      channelPartner: data.channel_partner,
      unizinTenantId: data.unizin_tenant_id,
      unizinStudentId: data.unizin_student_id,
      unizinProductId: data.unizin_product_id,
      unizinProductTitle: data.unizin_product_title,
      unizinContentId: data.unizin_content_id,
      unizinSectionId: data.unizin_section_id,
      unizinContext: data.unizin_context,
    };
  }

  /**
   * Makes the application aware of additional users that are not the current user.
   *
   * @method addOtherUser
   * @param {User} user - a user object
   */
  const addOtherUser = useCallback((user) => {
    setOtherUsers((otherUsers) => {
      const newOtherUsersObject = { ...otherUsers };
      newOtherUsersObject[user.pk] = user;

      return newOtherUsersObject;
    });

    return user;
  }, []);

  useEffect(
    function updateDeviceUUID() {
      setDeviceUUID(
        searchParams.has('deviceUUID')
          ? searchParams.get('deviceUUID') // allow a query param to override the device uuid
          : getLocalSettingsProxy().deviceUUID,
      );
    },
    [searchParams],
  );

  useEffect(function getAlertMessages() {
    fetch('/api/alert-messages/')
      .then((r) => r.json())
      .then(({ data }) => {
        if (!data?.length) {
          return;
        }

        const currentTime = new Date();

        const validData = data.filter(
          (d) =>
            d.attributes.active && // is Active
            currentTime >= new Date(d.attributes.start_time) && // start_time has passed, so ready to display
            currentTime < new Date(d.attributes.end_time), // end_time has not reached, so should be displaying
        );

        // If more than 1 valid Alert Message, sort by most recent one.
        const mostCurrentData = validData.sort(
          (a, b) =>
            new Date(b.attributes.start_time).getTime() -
            new Date(a.attributes.start_time).getTime(),
        );

        if (mostCurrentData.length) {
          setAlertMessage(mostCurrentData[0].attributes.message);
        }
      })
      .catch((e) => console.warn('Failed to fetch alert message', e));
  }, []);

  useEffect(
    function determineIfShouldAlwaysDownloadDocuments() {
      const mode = getLocalSettingsProxy().alwaysDownloadDocuments;

      if (!bootstrapData) {
        return;
      }

      if (mode === ALWAYS_DOWNLOAD_DOCUMENTS.FORCE_DISABLE) {
        setShouldAlwaysDownloadDocuments(false);
        return;
      }

      if (mode === ALWAYS_DOWNLOAD_DOCUMENTS.FORCE_ENABLE) {
        setShouldAlwaysDownloadDocuments(true);
        return;
      }

      setShouldAlwaysDownloadDocuments(
        isUserIdInPercentageThreshold(
          bootstrapData.user_id,
          bootstrapData.always_download_documents_percentage_of_users,
        ),
      );
    },
    [bootstrapData],
  );

  useEffect(
    function determineIfUseElasticsearchInsteadOfLunr() {
      if (!bootstrapData) {
        return;
      }

      setUseElasticsearchInsteadOfLunr(
        bootstrapData.use_elasticsearch_instead_of_lunr,
      );
    },
    [bootstrapData],
  );

  const values = useMemo(
    () => ({
      alertMessage,
      analyticsMetadata,
      backToShelfInfo,
      deviceUUID,
      isSessionExpired,
      organization,
      otherUsers,
      ownedBookPk,
      shouldAlwaysDownloadDocuments,
      useElasticsearchInsteadOfLunr,
      user,
    }),
    [
      alertMessage,
      analyticsMetadata,
      backToShelfInfo,
      deviceUUID,
      isSessionExpired,
      organization,
      otherUsers,
      ownedBookPk,
      shouldAlwaysDownloadDocuments,
      useElasticsearchInsteadOfLunr,
      user,
    ],
  );

  const modifiers = useMemo(
    () => ({
      addOtherUser,
      setIsSessionExpired,
    }),
    [addOtherUser, setIsSessionExpired],
  );

  // For debug
  window.v7.session = {
    ...values,
    ...modifiers,
  };

  return (
    <UserSessionValuesContext.Provider value={values}>
      <UserSessionModifiersContext.Provider value={modifiers}>
        {user ? children : <LoadingIndicatorForApp />}
      </UserSessionModifiersContext.Provider>
    </UserSessionValuesContext.Provider>
  );
};

export default UserSessionProvider;
